import { useCallback, useMemo, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import {
  GlobalSearchDocument,
  SearchPersonsDocument,
  type SearchPersonsQuery,
} from "@/graphql/generated/operations";

// Ranked, de-duplicated person ids from a globalSearch result set (globalSearch
// already returns rank order; a person can appear via multiple fragments).
function dedupePersonIds(
  results: ReadonlyArray<{ personId?: string | null }>,
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const r of results) {
    if (r.personId && !seen.has(r.personId)) {
      seen.add(r.personId);
      ids.push(r.personId);
    }
  }
  return ids;
}

// Person search backed by genesis' globalSearch (genesis#98). globalSearch is
// the authoritative source: it ranks across ALL public person fragments (so a
// multi-term query like "Tim TU München" matches even when the terms live in
// different profile fields, via the internal public aggregate fragment) and
// applies the structured personFilter server-side. It returns thin, ranked
// rows (personId + rank), so we hydrate the current set through SearchPersons
// (persons(where: id in …)) for the rich result cards, preserving rank order.
//
// Paging is offset-based (globalSearch has no cursors); loadMore advances skip
// and appends. The return shape mirrors usePagedQuery so it is drop-in for the
// list UI.

type PersonNode = SearchPersonsQuery["persons"]["nodes"][number];

// Loose alias for the structured filter: buildExtendedWhere() emits a
// PersonFilterInput-shaped object that the query layer casts at the call site
// (same pattern the rest of the search code uses).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PersonFilter = Record<string, any>;

interface GlobalPersonSearchResult {
  nodes: PersonNode[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMoreFailed: boolean;
  error: Error | undefined;
  loadMore: () => void;
  // One-shot fetch of the full ranked id set (up to `limit`) for the current
  // query + filter, bypassing pagination. `capped` is true when more matched
  // than `limit` could return (globalSearch overfetches limit+1 to derive it).
  fetchAllIds: (limit: number) => Promise<{ ids: string[]; capped: boolean }>;
}

export function useGlobalPersonSearch(opts: {
  query: string;
  personFilter: PersonFilter | null;
  pageSize?: number;
  skip?: boolean;
}): GlobalPersonSearchResult {
  const { query, personFilter, pageSize = 24, skip = false } = opts;
  const trimmed = query.trim();
  const client = useApolloClient();

  const baseInput = useMemo(
    () => ({
      query: trimmed.length > 0 ? trimmed : null,
      entityTypes: ["PERSON"],
      personFilter: personFilter ?? null,
    }),
    [trimmed, personFilter],
  );

  const searchInput = useMemo(
    () => ({ ...baseInput, skip: 0, take: pageSize }),
    [baseInput, pageSize],
  );

  const search = useQuery(GlobalSearchDocument, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variables: { input: searchInput as any },
    skip,
  });

  const results = useMemo(() => search.data?.globalSearch.results ?? [], [search.data]);
  const hasMore = search.data?.globalSearch.hasNextPage ?? false;

  const personIds = useMemo(() => dedupePersonIds(results), [results]);

  const hydration = useQuery(SearchPersonsDocument, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variables: { input: { where: { id: { in: personIds } }, first: personIds.length } as any },
    skip: skip || personIds.length === 0,
  });

  // Each loadMore grows the id set, so the hydration query refetches with new
  // args and hydration.data is briefly undefined. Fall back to previousData
  // (the prior, smaller page) so the visible list never collapses to empty and
  // re-expands (flicker) mid-load.
  const byId = useMemo(() => {
    const src = hydration.data?.persons.nodes ?? hydration.previousData?.persons.nodes ?? [];
    const map = new Map<string, PersonNode>();
    for (const n of src) map.set(n.id, n);
    return map;
  }, [hydration.data, hydration.previousData]);

  // Re-apply globalSearch rank order to the hydrated persons.
  const nodes = useMemo(
    () => personIds.map((id) => byId.get(id)).filter((n): n is PersonNode => Boolean(n)),
    [personIds, byId],
  );

  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    search
      .fetchMore({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        variables: { input: { ...searchInput, skip: results.length } as any },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            ...prev,
            globalSearch: {
              ...fetchMoreResult.globalSearch,
              results: [
                ...(prev.globalSearch?.results ?? []),
                ...(fetchMoreResult.globalSearch?.results ?? []),
              ],
            },
          };
        },
      })
      .catch(() => setLoadMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [hasMore, loadingMore, search, searchInput, results.length]);

  const fetchAllIds = useCallback(
    async (limit: number) => {
      const res = await client.query({
        query: GlobalSearchDocument,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        variables: { input: { ...baseInput, skip: 0, take: limit } as any },
        fetchPolicy: "no-cache",
      });
      return {
        ids: dedupePersonIds(res.data?.globalSearch.results ?? []),
        capped: res.data?.globalSearch.hasNextPage ?? false,
      };
    },
    [client, baseInput],
  );

  // Keep loading continuous across the two-phase (search -> hydrate) gap so the
  // header doesn't flash "Keine Treffer" between globalSearch resolving and the
  // first hydration arriving. Once any node is shown, loading is false.
  const firstPageLoading = search.loading && results.length === 0;
  const hydrating = personIds.length > 0 && nodes.length === 0;

  return {
    nodes,
    loading: firstPageLoading || hydrating,
    loadingMore,
    hasMore,
    loadMoreFailed,
    error: search.error ?? hydration.error,
    loadMore,
    fetchAllIds,
  };
}
